import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faCloud,
    faMusic,
    faSpinner,
    faGripVertical,
    faBars,
    faLink,
    faCircleCheck,
    faChartSimple,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import firebase from '../firebaseConfig';
import Helpers from '../Helpers';
import GradientThumb from './GradientThumb';
import CommentsCard from './CommentsCard';
import EditableName from './EditableName';
import FolderDropModal from './FolderDropModal';
import StorageLimitModal from './StorageLimitModal';
import { useAlbumArtURL } from '../hooks/useAlbumArtURL';
import { useShareDisplayMode } from '../hooks/useShareDisplayMode';
import { projectTarget, trackTarget } from '../shareTarget';
import { isChildElement, isAudioFile, AUDIO_ACCEPT } from '../utils';
import { extractFolderDrop, importGroupsAsProjects } from '../folderDrop';
import { getStorageQuota, partitionFilesByQuota } from '../storageQuota';
import { startBulkUpload } from '../uploads';

// "is-live" green for Public, "is-invite" amber for Invite Only, no class for Private.
function shareModeClass(mode) {
    if (mode === 'public') return 'is-live';
    if (mode === 'inviteOnly') return 'is-invite';
    return '';
}

function TrackShareButton({ track, project, onShareTrack }) {
    const target = trackTarget({ ...track, project });
    const mode = useShareDisplayMode(target);
    return (
        <span
            className={`track-more track-share-btn ${shareModeClass(mode)}`}
            title="Share this track"
            aria-label="Share this track"
            onClick={(e) => {
                e.stopPropagation();
                onShareTrack(track);
            }}
        >
            <FontAwesomeIcon icon={faLink} />
        </span>
    );
}

const FOLDER_DROP_SOFT_CAP = 200;

const WEIGHT_UPPER_LIMIT = 10;

function computeOrderWeight(lh, rh) {
    const mid = (lh + rh) / 2;
    const range = (rh - mid) / 100;
    const rand = Math.random() * range;
    return mid + rand;
}

const RightPanel = ({ project, tracks, setTracks, currentTrack, setCurrentTrack, getCurrentPosition, onSeek, onRenameProject, onRenameTrack, onOpenMenu, readOnly, onShare, onShareTrack, onInsights, onProjectsCreated, onDeleteProject, uploadProgress = {}, tracksLoading }) => {
    const [dragging, setDragging] = useState(false);
    const [folderDrop, setFolderDrop] = useState(null);
    const [storagePrompt, setStoragePrompt] = useState(null);
    const [draggedTrackIndex, setDraggedTrackIndex] = useState(null);
    const [dropTargetIndex, setDropTargetIndex] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const projectShareMode = useShareDisplayMode(
        project ? projectTarget({ id: project.id, artistId: project.artist?.id, name: project.name }) : null,
    );
    const linkSharingOn = projectShareMode === 'public';
    const linkSharingInvite = projectShareMode === 'inviteOnly';
    const fileInputRef = useRef(null);
    const headerArtUrl = useAlbumArtURL(project);

    // For anyone viewing via /p/{slug} — view OR editor — surface the URL
    // they used to get here so they can pass the link along. Share *settings*
    // (the full modal) stay owner-only, but the copy gesture should work
    // for any shared viewer.
    const isSharedView = !!project?.isShared;
    const sharedCopyUrl = isSharedView && project?.slug
        ? `https://open.demodeck.com/p/${project.slug}`
        : null;

    const copySharedLink = async () => {
        if (!sharedCopyUrl) return;
        try {
            await navigator.clipboard.writeText(sharedCopyUrl);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 1600);
        } catch (_e) { /* clipboard blocked; ignore */ }
    };

    if (!project) {
        return (
            <div className="right-panel empty">
                <button className="mobile-menu-btn empty-menu-btn" onClick={onOpenMenu} title="Menu" aria-label="Open menu">
                    <FontAwesomeIcon icon={faBars} />
                </button>
                <div className="right-empty-message">Pick a project</div>
            </div>
        );
    }

    // Pre-flight storage check. Returns the subset of `files` to upload — the
    // whole list when it fits, the user-confirmed subset when some files are
    // over plan, or [] when cancelled. Fails open on a lookup error: the
    // backend's reserveBytes is the real gate, so a flaky read shouldn't block.
    const gateByQuota = async (files) => {
        let quota;
        try {
            quota = await getStorageQuota();
        } catch (err) {
            console.error('Storage quota check failed', err);
            return files;
        }
        if (!quota) return files;
        const { accepted, rejected } = partitionFilesByQuota(files, quota.remaining);
        if (!rejected.length) return accepted;
        const overBytes = files.reduce((sum, f) => sum + f.size, 0) - quota.remaining;
        return new Promise((resolve) => {
            setStoragePrompt({
                rejected,
                acceptedCount: accepted.length,
                overBytes,
                onConfirm: () => { setStoragePrompt(null); resolve(accepted); },
                onCancel: () => { setStoragePrompt(null); resolve([]); },
            });
        });
    };

    // Uploads pre-vetted files into the current project.
    const uploadIntoProject = async (files) => {
        const newTracks = await startBulkUpload(project, files);
        if (newTracks.length) setTracks((prev) => [...prev, ...newTracks]);
    };

    const ingestFiles = async (fileList) => {
        const audioFiles = Array.from(fileList).filter(isAudioFile);
        if (!audioFiles.length) return;
        await uploadIntoProject(await gateByQuota(audioFiles));
    };

    const handleAddClick = () => fileInputRef.current?.click();

    const handleFilePick = (e) => {
        if (e.target.files?.length) {
            ingestFiles(e.target.files);
            e.target.value = '';
        }
    };

    const handleDragOver = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        if (!dragging) setDragging(true);
    };

    const handleDragLeave = (e) => {
        if (!isChildElement(e.currentTarget, e.relatedTarget)) {
            setDragging(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragging(false);

        const drop = await extractFolderDrop(e.dataTransfer);

        // Folders dropped — always ask: separate projects vs. this one.
        if (drop.hasFolders) {
            setFolderDrop(drop);
            return;
        }

        const { looseFiles } = drop;
        if (!looseFiles.length) return;
        if (looseFiles.length > FOLDER_DROP_SOFT_CAP) {
            const ok = window.confirm(
                `Upload ${looseFiles.length} audio files to "${project?.name || 'this project'}"?`
            );
            if (!ok) return;
        }
        ingestFiles(looseFiles);
    };

    const handleFolderSeparate = async (groups) => {
        const drop = folderDrop;
        setFolderDrop(null);
        // Quota spans every file in the drop — vet loose files and grouped
        // files together so the over-limit ones are flagged across the batch.
        const allFiles = [...drop.looseFiles, ...groups.flatMap((g) => g.files)];
        const toUpload = await gateByQuota(allFiles);
        if (!toUpload.length) return;
        const keep = new Set(toUpload);
        const looseKeep = drop.looseFiles.filter((f) => keep.has(f));
        const groupsKeep = groups
            .map((g) => ({ ...g, files: g.files.filter((f) => keep.has(f)) }))
            .filter((g) => g.files.length);
        if (looseKeep.length) await uploadIntoProject(looseKeep);
        if (groupsKeep.length) importGroupsAsProjects(project.artist, groupsKeep, onProjectsCreated);
    };

    const handleFolderConsolidate = () => {
        const drop = folderDrop;
        setFolderDrop(null);
        ingestFiles([...drop.topGroups.flatMap((g) => g.files), ...drop.looseFiles]);
    };

    const onTrackDragStart = (e, index) => {
        const track = tracks[index];
        if (track.isLocked || uploadProgress[track.id] != null) {
            e.preventDefault();
            return;
        }
        setDraggedTrackIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-demodeck-track', String(index));
    };

    const onTrackDragOver = (e, index) => {
        if (draggedTrackIndex == null) return;
        if (e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const lowerHalf = e.clientY > rect.top + rect.height / 2;
        const insertion = lowerHalf ? index + 1 : index;
        if (dropTargetIndex !== insertion) setDropTargetIndex(insertion);
    };

    const onTrackDrop = async (e) => {
        if (draggedTrackIndex == null || dropTargetIndex == null) {
            setDraggedTrackIndex(null);
            setDropTargetIndex(null);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const sourceIndex = draggedTrackIndex;
        const insertion = dropTargetIndex;
        setDraggedTrackIndex(null);
        setDropTargetIndex(null);
        // No-op when dropping back where it already was.
        if (insertion === sourceIndex || insertion === sourceIndex + 1) return;

        const reordered = [...tracks];
        const [moved] = reordered.splice(sourceIndex, 1);
        const adjusted = sourceIndex < insertion ? insertion - 1 : insertion;
        reordered.splice(adjusted, 0, moved);

        const left = reordered[adjusted - 1];
        const right = reordered[adjusted + 1];
        const lh = left?.orderWeight ?? 0;
        const maxWeight = reordered.reduce((m, t) => Math.max(m, t.orderWeight || 0), 0);
        const rh = right?.orderWeight ?? Math.max(WEIGHT_UPPER_LIMIT, maxWeight + 1);

        const newWeight = computeOrderWeight(lh, rh);
        moved.orderWeight = newWeight;

        setTracks(reordered);

        try {
            await firebase.database()
                .ref('artists').child(moved.project.artist.id)
                .child('projects').child(moved.project.id)
                .child('tracks').child(moved.id)
                .child('metadata').child('order weight')
                .set(newWeight);
        } catch (err) {
            console.error('Failed to reorder track', err);
        }
    };

    const onTrackDragEnd = () => {
        setDraggedTrackIndex(null);
        setDropTargetIndex(null);
    };

    return (
        <section
            className={`right-panel ${dragging ? 'dragging' : ''}`}
            key={project.id}
            onDragOver={readOnly ? undefined : handleDragOver}
            onDragLeave={readOnly ? undefined : handleDragLeave}
            onDrop={readOnly ? undefined : handleDrop}
        >
            <header className="right-header">
                <button className="mobile-menu-btn" onClick={onOpenMenu} title="Menu" aria-label="Open menu">
                    <FontAwesomeIcon icon={faBars} />
                </button>
                <GradientThumb id={project.id} imageUrl={headerArtUrl} className="right-header-art" />
                <div className="right-header-text">
                    <div className="right-header-eyebrow">
                        {readOnly && (
                            <span className={`access-chip ${project.permission === 'editor' ? 'edit' : 'view'}`}>
                                {project.permission === 'editor' ? 'Edit' : 'View only'}
                            </span>
                        )}
                        <span>TRACKS</span>
                    </div>
                    <h2 className="right-header-title">
                        {readOnly ? (
                            <span>{project.name || 'Untitled'}</span>
                        ) : (
                            <EditableName
                                value={project.name}
                                placeholder="Untitled"
                                onSave={(name) => onRenameProject?.(project, name)}
                            />
                        )}
                    </h2>
                </div>
                {(!readOnly || sharedCopyUrl) && (
                    <div className="right-header-actions">
                        {!readOnly && (
                            <button className="add-btn" onClick={handleAddClick}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Add Track</span>
                            </button>
                        )}
                        {!readOnly && onInsights && (
                            <button
                                className="add-btn add-btn-secondary"
                                onClick={() => onInsights(project)}
                                title="Insights"
                            >
                                <FontAwesomeIcon icon={faChartSimple} />
                                <span>Insights</span>
                            </button>
                        )}
                        {sharedCopyUrl && (
                            <button
                                className={`add-btn add-btn-secondary share-trigger-btn ${linkCopied ? 'is-live' : ''}`}
                                onClick={copySharedLink}
                                title={linkCopied ? 'Link copied' : "Copy this project's link"}
                            >
                                <FontAwesomeIcon icon={linkCopied ? faCircleCheck : faLink} />
                                <span>{linkCopied ? 'Link copied' : 'Copy link'}</span>
                            </button>
                        )}
                        {!isSharedView && onShare && (
                            <button
                                className={`add-btn add-btn-secondary share-trigger-btn ${shareModeClass(projectShareMode)}`}
                                onClick={() => onShare(project)}
                                title={
                                    linkSharingOn ? 'Link sharing is on'
                                        : linkSharingInvite ? 'Invite-only sharing is on'
                                            : 'Share'
                                }
                            >
                                {(linkSharingOn || linkSharingInvite) && (
                                    <span className="share-pulse-dot" aria-hidden="true" />
                                )}
                                <FontAwesomeIcon icon={faLink} />
                                <span>
                                    {linkSharingOn ? 'Link sharing is on'
                                        : linkSharingInvite ? 'Invite-only is on'
                                            : 'Share'}
                                </span>
                            </button>
                        )}
                        {!readOnly && !isSharedView && onDeleteProject && (
                            <button
                                className="add-btn add-btn-secondary add-btn-danger"
                                onClick={() => onDeleteProject(project)}
                                title="Delete project"
                                aria-label="Delete project"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        )}
                        {!readOnly && (
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={AUDIO_ACCEPT}
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFilePick}
                            />
                        )}
                    </div>
                )}
            </header>

            <div className="right-table">
                <div className="right-table-head">
                    <div className="col-num">#</div>
                    <div className="col-title">TITLE</div>
                    <div className="col-length">LENGTH</div>
                    <div className="col-more"></div>
                </div>

                {tracks.length === 0 && !tracksLoading && (
                    <div className="right-empty">No tracks yet — drag audio files here or click Add Track.</div>
                )}

                {tracksLoading && tracks.length === 0 && (
                    <div className="right-track-rows">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <div key={`skeleton-${i}`} className="right-track-row is-skeleton">
                                <div className="col-num">
                                    <span className="track-num-text">{i + 1}</span>
                                </div>
                                <div className="col-title">
                                    <div className="track-title-name">
                                        <span className={`skeleton-line ${i % 2 ? 'medium' : 'long'}`} />
                                    </div>
                                    <div className="track-title-sub">
                                        <span className="skeleton-line tiny" />
                                    </div>
                                </div>
                                <div className="col-length">
                                    <span className="skeleton-line tiny" />
                                </div>
                                <div className="col-more"></div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="right-track-rows">
                {tracks.map((track, i) => {
                    const isActive = currentTrack?.id === track.id;
                    const progress = uploadProgress[track.id];
                    const isUploading = progress != null;
                    const isBeingDragged = draggedTrackIndex === i;
                    const adjacent = dropTargetIndex === draggedTrackIndex || dropTargetIndex === draggedTrackIndex + 1;
                    const dropAbove = !adjacent && dropTargetIndex === i;
                    const dropBelow = !adjacent && dropTargetIndex === i + 1 && i === tracks.length - 1;
                    return (
                        <button
                            key={track.id}
                            draggable={!readOnly && !track.isLocked && !isUploading}
                            onDragStart={readOnly ? undefined : (e) => onTrackDragStart(e, i)}
                            onDragOver={readOnly ? undefined : (e) => onTrackDragOver(e, i)}
                            onDrop={readOnly ? undefined : onTrackDrop}
                            onDragEnd={readOnly ? undefined : onTrackDragEnd}
                            className={`right-track-row ${isActive ? 'active' : ''} ${track.isLocked ? 'locked' : ''} ${isUploading ? 'uploading' : ''} ${isBeingDragged ? 'being-dragged' : ''} ${dropAbove ? 'drop-above' : ''} ${dropBelow ? 'drop-below' : ''}`}
                            onClick={() => !track.isLocked && setCurrentTrack(track)}
                            style={isUploading ? { '--upload-progress': `${progress}%` } : undefined}
                        >
                            <div className="col-num">
                                {!readOnly && <FontAwesomeIcon icon={faGripVertical} className="track-grip" />}
                                <span className="track-num-text">{i + 1}</span>
                            </div>
                            <div className="col-title">
                                <div className="track-title-name">
                                    {readOnly ? (
                                        <span>{track.name || 'Untitled'}</span>
                                    ) : (
                                        <EditableName
                                            value={track.name}
                                            placeholder="Untitled"
                                            onSave={(name) => onRenameTrack?.(track, name)}
                                        />
                                    )}
                                </div>
                                <div className="track-title-sub">
                                    {isUploading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} className="track-spin" />
                                            <span>Uploading {progress}%</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCloud} />
                                            <span>Synced</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="col-length">{Helpers.formatDuration(track.duration || 0)}</div>
                            <div className="col-more">
                                {!readOnly && onShareTrack && (
                                    <TrackShareButton
                                        track={track}
                                        project={project}
                                        onShareTrack={onShareTrack}
                                    />
                                )}
                            </div>
                        </button>
                    );
                })}
                </div>
            </div>

            {currentTrack && !readOnly && (
                <CommentsCard
                    track={currentTrack}
                    getCurrentPosition={getCurrentPosition}
                    onSeek={onSeek}
                />
            )}

            {dragging && (
                <div className="dropzone-overlay">
                    <div className="dropzone-card">
                        <FontAwesomeIcon icon={faMusic} className="dropzone-icon" />
                        <div className="dropzone-title">Drop audio to add to {project.name || 'project'}</div>
                        <div className="dropzone-sub">Audio files only · Multiple supported</div>
                    </div>
                </div>
            )}

            {folderDrop && (
                <FolderDropModal
                    separateGroups={folderDrop.folderGroups}
                    combined={{
                        title: 'Add to this project',
                        description: `Everything goes into ${project.name || 'this project'}`,
                        action: 'Add to project',
                        rows: [{
                            name: project.name || 'Untitled',
                            trackCount:
                                folderDrop.topGroups.reduce((s, g) => s + g.files.length, 0)
                                + folderDrop.looseFiles.length,
                            existing: true,
                        }],
                    }}
                    onSeparate={handleFolderSeparate}
                    onCombined={handleFolderConsolidate}
                    onCancel={() => setFolderDrop(null)}
                />
            )}

            {storagePrompt && (
                <StorageLimitModal
                    rejected={storagePrompt.rejected}
                    acceptedCount={storagePrompt.acceptedCount}
                    overBytes={storagePrompt.overBytes}
                    onConfirm={storagePrompt.onConfirm}
                    onCancel={storagePrompt.onCancel}
                />
            )}
        </section>
    );
};

export default RightPanel;
